import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  XCircle, 
  AlertTriangle, 
  RefreshCw, 
  ArrowLeft,
  Phone,
  Mail,
  HelpCircle
} from 'lucide-react';
import api, { getImageUrl } from '../utils/api';
import { MetaPixelService } from '../services/metaPixel';

interface FailedOrder {
  _id: string;
  totalAmount: number;
  paymentMethod: string;
  failureReason?: string;
  createdAt: string;
  items: Array<{
    product: {
      _id: string;
      name: string;
      images: string[];
      price: number;
    };
    quantity: number;
    price: number;
  }>;
}

const OrderFailed: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<FailedOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
        
        // Track failed purchase
        const contentIds = response.data.items.map((item: any) => item.product._id);
        MetaPixelService.trackCustomEvent('FailedPayment', {
          content_ids: contentIds,
          value: response.data.totalAmount,
          currency: 'INR',
          failure_reason: response.data.failureReason || 'Unknown'
        });
      } catch (error: any) {
        console.error('Error fetching order:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleRetryPayment = async () => {
    if (!order) return;

    setIsRetrying(true);
    try {
      const paymentResponse = await api.post('/payment/initiate-upi', {
        orderId: order._id,
        amount: order.totalAmount
      });
      
      // Redirect to payment gateway
      window.location.href = paymentResponse.data.paymentUrl;
    } catch (error: any) {
      console.error('Error retrying payment:', error);
      alert('Failed to retry payment. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFailureReason = () => {
    if (order?.failureReason) {
      return order.failureReason;
    }
    return 'Payment could not be processed. This might be due to insufficient funds, network issues, or bank-related problems.';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Failure Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full mb-4">
            <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Payment Failed
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            We're sorry, but your payment could not be processed.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Order Details & Error Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Error Information */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              <div className="flex items-start">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
                    Payment Processing Error
                  </h3>
                  <p className="text-red-700 dark:text-red-300 mb-4">
                    {getFailureReason()}
                  </p>
                  {order && (
                    <div className="text-sm text-red-600 dark:text-red-400">
                      <p>Order ID: #{order._id.slice(-8).toUpperCase()}</p>
                      <p>Failed at: {formatDate(order.createdAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary */}
            {order && (
              <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Order Summary
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Order ID</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">#{order._id.slice(-8).toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Amount</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">₹{order.totalAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{order.paymentMethod}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400">
                      Failed
                    </span>
                  </div>
                </div>

                {/* Items */}
                <div className="border-t border-gray-200 dark:border-secondary-600 pt-4">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Items in Your Order</h3>
                  <div className="space-y-4">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex items-center space-x-4">
                        <img
                          src={getImageUrl(item.product.images[0])}
                          alt={item.product.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">{item.product.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Qty: {item.quantity} × ₹{item.price.toLocaleString()}
                          </p>
                        </div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* What Happened */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What Happened?
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>Your order was created but the payment could not be completed</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>No amount has been deducted from your account</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p>Your cart items are still saved and available</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What would you like to do?
              </h2>
              <div className="space-y-3">
                {order?.paymentMethod === 'UPI' && (
                  <button
                    onClick={handleRetryPayment}
                    disabled={isRetrying}
                    className="w-full flex items-center justify-center p-3 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                             text-white rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isRetrying ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {isRetrying ? 'Processing...' : 'Retry Payment'}
                  </button>
                )}
                
                <Link
                  to="/cart"
                  className="w-full flex items-center justify-center p-3 border border-gray-300 dark:border-secondary-600 
                           text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Cart
                </Link>
                
                <Link
                  to="/products"
                  className="w-full flex items-center justify-center p-3 border border-gray-300 dark:border-secondary-600 
                           text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>

            {/* Common Issues */}
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <HelpCircle className="h-5 w-5 mr-2" />
                Common Issues
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Insufficient Balance</h4>
                  <p className="text-gray-600 dark:text-gray-400">Check if your account has enough funds</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Network Issues</h4>
                  <p className="text-gray-600 dark:text-gray-400">Poor internet connection during payment</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Bank Restrictions</h4>
                  <p className="text-gray-600 dark:text-gray-400">Your bank may have blocked the transaction</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">Card Expiry</h4>
                  <p className="text-gray-600 dark:text-gray-400">Check if your card is still valid</p>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6">
              <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2 flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                Need Help?
              </h3>
              <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
                If you continue to face issues, our support team is here to help.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-orange-700 dark:text-orange-300">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>Call: 1800-XXX-XXXX</span>
                </div>
                <div className="flex items-center text-orange-700 dark:text-orange-300">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>Email: support@example.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderFailed;